import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import { Model } from "survey-core";
import PageTitle from "utils/PageTitle";
import StepperComponent from "../OrderKitStepper";
import StepperLayout from "../SideMenuLayout";
import InformedConsentSurvey from "components/v3/sharedComponents/surveys/InformedConsentSurvey";
import { selectAllPreviousSurveyAnswers } from "store/reducers/survey/SurveySlice";
import {
  selectTestKitsGLIsCurrentlyOffering,
  selectExistingOrdersWithoutCancelled
} from "store/reducers/order/OrderInformationSlice";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import { testKitUtils } from "utils/v3/testKitUtils";
import { PATHS } from "constants/pathConstants";

const OrderKitSurveyPage = () => {
  const history = useHistory();
  const kitSuggestionOverrideToken = useLocation().search;
  const kitsGLCurrentOffers = useSelector(selectTestKitsGLIsCurrentlyOffering);
  const existingTestKitOrders = useSelector(
    selectExistingOrdersWithoutCancelled
  );
  const previousSurveyAnswers = useSelector(selectAllPreviousSurveyAnswers);
  const userInformation = useSelector(selectUserPersonalInformation);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [surveyModel, setSurveyModel] = useState<any>();
  const handleCurrentPageChanging = useCallback(
    (surveyModel: Model, options: Record<string, any>) => {
      setLoaded(false);
      setCurrentStepIndex(
        options?.isNextPage ? currentStepIndex + 1 : currentStepIndex - 1
      );
    },
    [currentStepIndex, setCurrentStepIndex, setLoaded]
  );

  const kitsToOfferMemberRightNow = testKitUtils.figureOutKitsToOfferMemberRightNow(
    kitsGLCurrentOffers,
    existingTestKitOrders,
    previousSurveyAnswers,
    kitSuggestionOverrideToken,
    userInformation.glProgramId
  );
  const hasKitsToOfferMemberRightNow = kitsToOfferMemberRightNow.length > 0;

  PageTitle("Cancer diagnosis survey");

  useEffect(() => {
    // If these aren't satisfied, we can't offer kits before a consult
    if (!hasKitsToOfferMemberRightNow) {
      history.push(PATHS.LANDING_PAGE);
    }
  }, []);

  return (
    <StepperLayout
      loaded={loaded}
      onboardingStepper={
        <StepperComponent
          activeStepIndex={currentStepIndex}
          onBack={() => {
            surveyModel.prevPage();
          }}
        />
      }
    >
      <Box
        sx={{
          alignItems: {
            xs: "center",
            lg: "flex-start"
          },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: {
            xs: "30px",
            lg: 8
          },
          marginTop: 0,
          paddingTop: {
            xs: "30px",
            lg: 0
          },
          paddingX: {
            xs: "20px",
            lg: 0
          }
        }}
      >
        <InformedConsentSurvey
          onCurrentPageChanging={handleCurrentPageChanging}
          onSurveyInstantiated={(surveyModel: Model) => {
            setCurrentStepIndex(surveyModel.currentPageNo);
            setLoaded(true);
            setSurveyModel(surveyModel);
          }}
        />
      </Box>
    </StepperLayout>
  );
};

export default OrderKitSurveyPage;
